import { useState, useEffect } from 'react';
import type { FC, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminFetch } from '../utils/api';
import { formatCents } from '../utils/format';

interface Affiliate {
  code: string;
  name: string;
  commissionPercent: number;
}

interface UserDeposit {
  email: string;
  cognitoUserId: string;
  depositCents: number;
}

interface Payout {
  code: string;
  name: string;
  email: string;
  commissionPercent: number;
  referredUserCount: number;
  totalDepositCents: number;
  payoutCents: number;
  users: UserDeposit[];
}

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

const Deposits: FC = () => {
  const { token } = useAuth();
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [month, setMonth] = useState(currentMonth());

  const [payout, setPayout] = useState<Payout | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!token) return;
    adminFetch<{ affiliates: Affiliate[] }>('/admin/affiliates', token)
      .then(data => setAffiliates(data.affiliates))
      .catch(err => console.error('Failed to load affiliates:', err));
  }, [token]);

  const handleFetch = async (e: FormEvent) => {
    e.preventDefault();
    if (!token || !affiliateCode || !month) return;
    setLoading(true);
    setError('');
    setPayout(null);
    setFetched(false);
    try {
      const data = await adminFetch<{ payouts: Payout[] }>(
        `/admin/payouts/summary?month=${month}`,
        token,
      );
      const match = data.payouts.find(p => p.code === affiliateCode);
      setPayout(match || null);
      setFetched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load deposits');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3">
      <form onSubmit={handleFetch} className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <select
          className="form-select form-select-sm"
          style={{ maxWidth: 400 }}
          value={affiliateCode}
          onChange={e => setAffiliateCode(e.target.value)}
        >
          <option value="">Select affiliate...</option>
          {affiliates.map(a => (
            <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
          ))}
        </select>
        <input
          type="month"
          className="form-control form-control-sm"
          style={{ width: 160 }}
          value={month}
          onChange={e => setMonth(e.target.value)}
        />
        <button type="submit" className="btn btn-primary btn-sm" disabled={loading || !affiliateCode || !month}>
          {loading ? '...' : 'Fetch'}
        </button>
        {payout && (
          <span className="ms-auto fw-bold text-success">
            Payout: {formatCents(payout.payoutCents)} ({payout.commissionPercent}%)
          </span>
        )}
      </form>

      {error && <div className="alert alert-warning py-2 small">{error}</div>}

      {fetched && payout && (
        <table className="table table-striped table-sm align-middle">
          <thead className="table-dark">
            <tr>
              <th>User</th>
              <th className="text-end">Deposits</th>
            </tr>
          </thead>
          <tbody>
            {payout.users.map(u => (
              <tr key={u.cognitoUserId}>
                <td className="small">{u.email}</td>
                <td className="text-end">{formatCents(u.depositCents)}</td>
              </tr>
            ))}
            {payout.users.length === 0 && (
              <tr><td colSpan={2} className="text-center text-muted">No deposits in {month}</td></tr>
            )}
            {payout.users.length > 0 && (
              <tr className="table-active">
                <td className="fw-bold">Total</td>
                <td className="text-end fw-bold">{formatCents(payout.totalDepositCents)}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {fetched && !payout && (
        <div className="text-muted small">No payout record for that affiliate.</div>
      )}
    </div>
  );
};

export default Deposits;
